import React, { createContext, useContext, useState, useCallback, ReactNode, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { API_ENDPOINTS } from '../config/api';

// =====================================================
// INTERFACES
// =====================================================

export interface SystemNotification {
  id: string;
  user_id: string;
  title: string;
  message: string;
  type: 'info' | 'success' | 'warning' | 'error';
  is_read: boolean;
  data?: any;
  created_at: string;
}

// =====================================================
// CONTEXT INTERFACE
// =====================================================

interface NotificationContextType {
  // State
  notifications: SystemNotification[];
  unreadCount: number;
  loading: boolean;
  error: string | null;

  // Actions
  fetchNotifications: () => Promise<void>;
  markAsRead: (notificationId: string) => Promise<void>;
  markAllAsRead: () => Promise<void>;
  clearError: () => void;
  requestNotificationPermission: () => Promise<boolean>;
}

// =====================================================
// CONTEXT CREATION
// =====================================================

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

// =====================================================
// PROVIDER COMPONENT
// =====================================================

export const NotificationProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [notifications, setNotifications] = useState<SystemNotification[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Calculate unread count
  const unreadCount = notifications.filter(n => !n.is_read).length;

  // Fetch notifications
  const fetchNotifications = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Get JWT token from localStorage
      const token = localStorage.getItem('token');
      if (!token) {
        console.log('No JWT token found - user not authenticated yet');
        return;
      }

      // Get user profile from the token
      const response = await fetch(`${API_ENDPOINTS.BASE}/api/auth/profile`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to get user profile');
      }

      const profileData = await response.json();
      const userId = profileData.profile.id;

      // Fetch notifications for this user
      const { data, error } = await supabase
        .from('notifications')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false })
        .limit(50);

      if (error) throw error;
      setNotifications(data || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch notifications');
    } finally {
      setLoading(false);
    }
  }, []);

  // Set up real-time subscription for notifications
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) return;

    let userId: string | null = null;

    // Get user ID first
    const setupSubscription = async () => {
      try {
        const response = await fetch(`${API_ENDPOINTS.BASE}/api/auth/profile`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (!response.ok) return;

        const profileData = await response.json();
        userId = profileData.profile.id;

        if (!userId) return;

        // Set up real-time subscription
        const channel = supabase
          .channel(`notifications_${userId}`)
          .on(
            'postgres_changes',
            {
              event: '*', // Listen for INSERT, UPDATE, DELETE
              schema: 'public',
              table: 'notifications',
              filter: `user_id=eq.${userId}`
            },
            (payload) => {
              console.log('Real-time notification update:', payload);
              // Refresh notifications when changes occur
              fetchNotifications();
            }
          )
          .subscribe((status) => {
            console.log('Notification subscription status:', status);
          });

        return () => {
          supabase.removeChannel(channel);
        };
      } catch (error) {
        console.error('Error setting up notification subscription:', error);
      }
    };

    const cleanup = setupSubscription();

    return () => {
      cleanup?.then(cleanupFn => cleanupFn?.());
    };
  }, [fetchNotifications]);

  // Mark notification as read
  const markAsRead = useCallback(async (notificationId: string) => {
    try {
      const { error } = await supabase
        .from('notifications')
        .update({ is_read: true })
        .eq('id', notificationId);

      if (error) throw error;

      // Update local state
      setNotifications(prev =>
        prev.map(n => n.id === notificationId ? { ...n, is_read: true } : n)
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to mark notification as read');
    }
  }, []);

  // Mark all notifications as read
  const markAllAsRead = useCallback(async () => {
    try {
      // Get user ID
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('No JWT token found');
      }

      const response = await fetch(`${API_ENDPOINTS.BASE}/api/auth/profile`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to get user profile');
      }

      const profileData = await response.json();
      const userId = profileData.profile.id;

      // Mark all notifications as read
      const { error } = await supabase
        .from('notifications')
        .update({ is_read: true })
        .eq('user_id', userId)
        .eq('is_read', false);

      if (error) throw error;

      // Update local state
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to mark all notifications as read');
    }
  }, []);

  // Clear error
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // Request browser notification permission
  const requestNotificationPermission = useCallback(async () => {
    if ('Notification' in window) {
      const permission = await Notification.requestPermission();
      return permission === 'granted';
    }
    return false;
  }, []);

  // Set up real-time subscription for new notifications
  useEffect(() => {
    let currentUserId = '';

    // Get current user ID for filtering notifications
    const getCurrentUserId = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) return;

        const response = await fetch(`${API_ENDPOINTS.BASE}/api/auth/profile`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (response.ok) {
          const profileData = await response.json();
          currentUserId = profileData.profile.id;
        }
      } catch (error) {
        console.error('Error getting current user ID:', error);
      }
    };

    // Get user ID first
    getCurrentUserId();

    const channel = supabase
      .channel('notifications')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'notifications'
        },
        (payload) => {
          const newNotification = payload.new as SystemNotification;

          // Add to notifications if it's for the current user
          if (newNotification.user_id === currentUserId) {
            setNotifications(prev => [newNotification, ...prev]);

            // Show browser notification if permission granted
            if (Notification.permission === 'granted') {
              const notificationOptions: NotificationOptions = {
                body: newNotification.message,
                icon: '/stashit-icon.svg',
                badge: '/stashit-icon.svg',
                tag: newNotification.id,
                requireInteraction: newNotification.data?.action === 'new_errand', // Keep new errand notifications visible
                data: newNotification.data
              };

              const browserNotification = new Notification(newNotification.title, notificationOptions);

              // Handle click on browser notification
              browserNotification.onclick = () => {
                window.focus();
                browserNotification.close();

                // Navigate based on notification action
                if (newNotification.data?.action === 'new_errand' && newNotification.data?.errand_id) {
                  window.location.href = '/driver/errands';
                } else if (newNotification.data?.action === 'errand_accepted' && newNotification.data?.errand_id) {
                  window.location.href = `/chat?errand=${newNotification.data.errand_id}`;
                } else if (newNotification.data?.errand_id) {
                  window.location.href = `/chat?errand=${newNotification.data.errand_id}`;
                }
              };
            }
          }
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'notifications'
        },
        (payload) => {
          const updatedNotification = payload.new as SystemNotification;

          // Update local state if it's for the current user
          if (updatedNotification.user_id === currentUserId) {
            setNotifications(prev =>
              prev.map(n => n.id === updatedNotification.id ? updatedNotification : n)
            );
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, []);

  // Load notifications on mount
  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  const value: NotificationContextType = {
    notifications,
    unreadCount,
    loading,
    error,
    fetchNotifications,
    markAsRead,
    markAllAsRead,
    clearError,
    requestNotificationPermission
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};

// =====================================================
// HOOK
// =====================================================

export const useNotifications = (): NotificationContextType => {
  const context = useContext(NotificationContext);
  if (context === undefined) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
};
